import { createContext, useCallback, useContext, useState } from 'react';
import { ErrorSnackbar, SuccessSnackbar } from 'sharedComponents/Snackbar';

type ToastSeverity = 'error' | 'success';

interface ToastState {
  open: boolean;
  message: string;
  severity: ToastSeverity;
  dataTestId: string;
}

interface ToastContextType {
  showToast: (message: string, severity: ToastSeverity, dataTestId: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const INITIAL_STATE: ToastState = {
  open: false,
  message: '',
  severity: 'error',
  dataTestId: '',
};

export const ToastProvider: React.FC = ({ children }) => {
  const [toast, setToast] = useState<ToastState>(INITIAL_STATE);

  const showToast = useCallback((message: string, severity: ToastSeverity, dataTestId: string) => {
    setToast({ open: true, message, severity, dataTestId });
  }, []);

  const handleClose = () => {
    setToast(INITIAL_STATE);
  };

  const SnackbarComponent = toast.severity === 'error' ? ErrorSnackbar : SuccessSnackbar;

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <SnackbarComponent
        open={toast.open}
        message={toast.message}
        onClose={handleClose}
        dataTestId={toast.dataTestId}
      />
    </ToastContext.Provider>
  );
};
